import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { ServiceConstants } from "constant";
import { BankSelectionService, ContractService } from "services";
import { Bank, BankAttachmentResponse } from "types";

import { ContractAttachment } from "./types";

const {
  REQUEST_BANK_ATTACHMENT_LIST_ID,
  REQUEST_BANK_ATTACHMENT_UPLOAD_ID,
  REQUEST_CONTRACT_ATTACHMENT_LIST_ID,
  REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID,
  REQUEST_SUCCESS,
} = ServiceConstants;

export const useContractAttachments = (
  bankCreditRequestId: number,
  bank: Bank,
  contractId: number | undefined
) => {
  const [zinzDocument, setZinzDocument] = useState<BankAttachmentResponse | undefined>();
  const [unsignedContracts, setUnsignedContracts] = useState<ContractAttachment[]>([]);
  const [signedContract, setSignedContract] = useState<ContractAttachment | undefined>();
  const [otherDocuments, setOtherDocuments] = useState<ContractAttachment[]>([]);

  const unsignedUploadRequestKey = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_UNSIGNED_" + bank;
  const signedUploadRequestKey = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_SIGNED";
  const otherUploadRequestKey = REQUEST_CONTRACT_ATTACHMENT_UPLOAD_ID + "_OTHER";
  const contractAttachmentsRequestKey = contractId
    ? REQUEST_CONTRACT_ATTACHMENT_LIST_ID + "_" + contractId
    : undefined;

  const attachmentsResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_ATTACHMENT_LIST_ID]
  );
  const zinzUploadResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_ATTACHMENT_UPLOAD_ID + "_ZINZ"]
  );
  const contractAttachmentsResponse = useSelector(
    (state: any) =>
      contractAttachmentsRequestKey ? state.service.object[contractAttachmentsRequestKey] : undefined
  );
  const contractUploadResponse = useSelector(
    (state: any) => state.service.object[unsignedUploadRequestKey]
  );
  const signedUploadResponse = useSelector(
    (state: any) => state.service.object[signedUploadRequestKey]
  );
  const otherUploadResponse = useSelector(
    (state: any) => state.service.object[otherUploadRequestKey]
  );

  // Reset on bank change
  useEffect(() => {
    setZinzDocument(undefined);
    setUnsignedContracts([]);
    setSignedContract(undefined);
    setOtherDocuments([]);
  }, [bank]);

  // Fetch contract attachments when contractId becomes available
  useEffect(() => {
    if (contractId) {
      ContractService.getContractAttachments(contractId);
    }
  }, [contractId]);

  // Handle ZINZ attachment list
  useEffect(() => {
    const data = attachmentsResponse?.data;
    if (!data) return;
    const attachments: BankAttachmentResponse[] = Array.isArray(data)
      ? data
      : Array.isArray(data.attachments)
      ? data.attachments
      : [];
    setZinzDocument(attachments.find((a) => a.subtype === "ZINZ"));
  }, [attachmentsResponse]);

  // Refresh bank attachments after ZINZ upload
  useEffect(() => {
    if (zinzUploadResponse?.data && bankCreditRequestId) {
      BankSelectionService.bankAttachmentsAll(bankCreditRequestId, bank);
    }
  }, [zinzUploadResponse, bankCreditRequestId, bank]);

  // Handle contract attachments response
  useEffect(() => {
    if (!contractAttachmentsResponse?.data || !Array.isArray(contractAttachmentsResponse.data)) return;

    const mapAttachment = (att: any): ContractAttachment => ({
      id: att.id,
      name: att.name,
      extension: att.extension,
      size: att.size,
      path: att.path,
      createdAt: att.info?.createdAt,
    });

    const sortByCreatedAtDesc = (a: ContractAttachment, b: ContractAttachment) =>
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

    setUnsignedContracts(
      contractAttachmentsResponse.data
        .filter((att: any) => att.type === "UNSIGNED_CONTRACT")
        .map(mapAttachment)
        .sort(sortByCreatedAtDesc)
    );
    setSignedContract(
      contractAttachmentsResponse.data
        .filter((att: any) => att.type === "SIGNED_CONTRACT")
        .map(mapAttachment)
        .sort(sortByCreatedAtDesc)[0]
    );
    setOtherDocuments(
      contractAttachmentsResponse.data
        .filter((att: any) => att.type === "OTHER")
        .map(mapAttachment)
        .sort(sortByCreatedAtDesc)
    );
  }, [contractAttachmentsResponse]);

  // Refresh after unsigned upload (first upload may also create the contract)
  useEffect(() => {
    if (contractUploadResponse?.status === REQUEST_SUCCESS && bankCreditRequestId && bank) {
      ContractService.getContract(bankCreditRequestId, bank);
      if (contractId) {
        ContractService.getContractAttachments(contractId);
      }
    }
  }, [bank, bankCreditRequestId, contractId, contractUploadResponse?.status]);

  // Refresh after other/signed uploads
  useEffect(() => {
    if (otherUploadResponse?.data && contractId) {
      ContractService.getContractAttachments(contractId);
    }
  }, [otherUploadResponse, contractId]);

  useEffect(() => {
    if (signedUploadResponse?.data && contractId) {
      ContractService.getContractAttachments(contractId);
    }
  }, [signedUploadResponse, contractId]);

  // ZINZ handlers
  const handleZinzUpload = (file: File) => {
    if (bankCreditRequestId) {
      BankSelectionService.bankAttachmentUploadZinz(bankCreditRequestId, bank, file);
    }
  };

  const handleZinzDelete = () => {
    if (zinzDocument?.id) {
      setZinzDocument(undefined);
      BankSelectionService.bankAttachmentDelete(zinzDocument.id);
    }
  };

  const handleZinzDownload = () => {
    if (zinzDocument?.path) {
      window.open(zinzDocument.path, "_blank");
    }
  };

  // Unsigned contract handlers
  const handleUnsignedContractUpload = (file: File) => {
    if (bankCreditRequestId && bank) {
      ContractService.uploadUnsignedContract(bankCreditRequestId, bank, file);
    }
  };

  const handleUnsignedContractDownload = async (attachmentId: string) => {
    const attachment = unsignedContracts.find((doc) => doc.id === attachmentId);
    if (!attachment) return;
    await ContractService.downloadContractAttachment(attachment.id, attachment.name, attachment.extension);
  };

  const handleUnsignedContractDelete = (attachmentId: string) => {
    ContractService.deleteContractAttachment(attachmentId);
    setUnsignedContracts((prev) => prev.filter((doc) => doc.id !== attachmentId));
  };

  // Signed contract handlers
  const handleSignedContractUpload = (file: File) => {
    if (contractId) {
      ContractService.uploadSignedContract(contractId, file);
    }
  };

  const handleSignedContractDownload = async () => {
    if (!signedContract) return;
    await ContractService.downloadContractAttachment(
      signedContract.id,
      signedContract.name,
      signedContract.extension
    );
  };

  const handleSignedContractDelete = () => {
    if (!signedContract) return;
    ContractService.deleteContractAttachment(signedContract.id);
    setSignedContract(undefined);
  };

  // Other document handlers
  const handleOtherDocumentUpload = (file: File) => {
    if (contractId) {
      ContractService.uploadOtherAttachment(contractId, file);
    }
  };

  const handleOtherDocumentDownload = async (document: ContractAttachment) => {
    await ContractService.downloadContractAttachment(document.id, document.name, document.extension);
  };

  const handleOtherDocumentDelete = (attachmentId: string) => {
    ContractService.deleteContractAttachment(attachmentId);
    setOtherDocuments((prev) => prev.filter((doc) => doc.id !== attachmentId));
  };

  return {
    zinzDocument,
    unsignedContracts,
    signedContract,
    otherDocuments,
    handleZinzUpload,
    handleZinzDelete,
    handleZinzDownload,
    handleUnsignedContractUpload,
    handleUnsignedContractDownload,
    handleUnsignedContractDelete,
    handleSignedContractUpload,
    handleSignedContractDownload,
    handleSignedContractDelete,
    handleOtherDocumentUpload,
    handleOtherDocumentDownload,
    handleOtherDocumentDelete,
  };
};
